import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import VertLogo from "@/components/VertLogo";

const Footer = () => {
  const { t, locale } = useTranslation();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="section-dark grain relative">
      <div className="shimmer-line" />

      <div className="container-wide pt-24 pb-12 md:pt-32 md:pb-14 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-12 md:gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center mb-6 group">
              <VertLogo className="h-5 w-auto text-[hsl(var(--sd-fg))] transition-opacity duration-300 group-hover:opacity-60" />
            </Link>
            <p className="text-[12px] text-[hsl(var(--sd-fg)/0.25)] leading-[1.75] max-w-[200px]">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h2 className="font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.55)] mb-6">{t("footer.engagements")}</h2>
            <ul className="space-y-3.5 text-[12px] text-[hsl(var(--sd-fg)/0.35)]">
              <li><Link to="/services/agentic-ai" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("services.offer1Title")}</Link></li>
              <li><Link to="/services/generative-ai" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("services.offer2Title")}</Link></li>
              <li><Link to="/services/predictive-ai" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("services.offer3Title")}</Link></li>
              <li><Link to="/services/data-platforms" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("services.offer4Title")}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.55)] mb-6">{t("nav.solutions")}</h2>
            <ul className="space-y-3.5 text-[12px] text-[hsl(var(--sd-fg)/0.35)]">
              <li><Link to="/solutions/ai-agents-workflow-automation" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Agents IA" : "AI Agents"}</Link></li>
              <li><Link to="/solutions/document-intelligence" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Intelligence documentaire" : "Document Intelligence"}</Link></li>
              <li><Link to="/solutions/knowledge-systems-enterprise-retrieval" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Gestion du savoir" : "Knowledge Systems"}</Link></li>
              <li><Link to="/solutions/predictive-models-decision-science" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Modèles prédictifs" : "Predictive Models"}</Link></li>
              <li><Link to="/solutions/data-platforms-for-ai" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Plateformes de données" : "Data Platforms"}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.55)] mb-6">{t("footer.company")}</h2>
            <ul className="space-y-3.5 text-[12px] text-[hsl(var(--sd-fg)/0.35)]">
              <li><Link to="/about" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("nav.about")}</Link></li>
              <li><Link to="/industries" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">Industries</Link></li>
              <li><Link to="/insights" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("nav.insights")}</Link></li>
              <li><Link to="/contact" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div>
            <h2 className="font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.55)] mb-6">{locale === "fr" ? "Ressources" : "Resources"}</h2>
            <ul className="space-y-3.5 text-[12px] text-[hsl(var(--sd-fg)/0.35)]">
              <li><Link to="/insights/vet-ai-vendor" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Évaluer un fournisseur IA" : "How to Vet an AI Vendor"}</Link></li>
              <li><Link to="/insights/production-grade-ai" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "IA en production" : "Production-Grade AI"}</Link></li>
              <li><Link to="/insights/workflows-deserve-ai" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">{locale === "fr" ? "Quels processus méritent l'IA" : "Which Workflows Deserve AI"}</Link></li>
            </ul>
            <h2 className="font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.55)] mb-4 mt-8">{t("footer.contactLabel")}</h2>
            <ul className="space-y-3.5 text-[12px] text-[hsl(var(--sd-fg)/0.35)]">
              <li><a href="mailto:guillaume.belisle@vert-ai.com" className="hover:text-[hsl(var(--sd-fg)/0.65)] transition-colors duration-400">guillaume.belisle@vert-ai.com</a></li>
              <li>{t("contact.location")}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-wide pb-8 relative z-10">
        <div className="h-px bg-[hsl(var(--sd-fg)/0.035)] mb-7" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-[hsl(var(--sd-fg)/0.18)]">
          <p>© {new Date().getFullYear()} {t("footer.copyright")}</p>
          <div className="flex items-center gap-6">
            <p className="font-mono text-[10px] tracking-[0.12em]">{t("footer.bottomTagline")}</p>
            <button
              onClick={scrollToTop}
              className="w-7 h-7 rounded-sm border border-[hsl(var(--sd-fg)/0.15)] flex items-center justify-center text-[hsl(var(--sd-fg)/0.55)] hover:text-[hsl(var(--sd-fg)/0.85)] hover:border-primary/40 transition-all duration-500 hover:shadow-[0_0_12px_hsl(var(--primary)/0.08)]"
              aria-label="Back to top"
            >
              <ArrowUp className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
